import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import type {
  TiptapFormValues,
  TrixFormValues,
} from "@/react/components/CommentsCompass/types";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { reactQueryPatch } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { commentCache } from "@/react/lib/queries/comments";
import type { Comment } from "@/react/types/Comment";

type UpdateCommentPayload = TiptapFormValues | TrixFormValues;

interface UseUpdateCommentParams {
  comment: Comment;
  onSuccess: () => void;
}

export const useUpdateComment = ({
  comment,
  onSuccess,
}: UseUpdateCommentParams) => {
  const { id, post_id: postId } = comment;
  const { success, error } = useToast();

  const { mutateAsync: updateComment } = useMutation<
    Comment,
    unknown,
    UpdateCommentPayload
  >({
    mutationFn: commentData =>
      reactQueryPatch<Comment, { comment: UpdateCommentPayload }>(
        internalApi.comments.update({ postId, id }),
        { comment: commentData },
      ),
    onSuccess: commentData => {
      commentCache.update({
        postId,
        id,
        updater: current => ({ ...commentData, replies: current.replies }),
      });
      onSuccess();
      success(t("comments.edit.alerts.success"));
    },
    onError: () => {
      error(t("comments.edit.alerts.failure"));
    },
  });

  return { updateComment };
};
